import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store/store";
import CashRegisterService from "../../services/cash/cashRegisterService";

import {
    setCashRegisters,
    setSelectedCashRegister,
    setLoading,

    setCashRegisterForm,
    updateCashRegisterField,
    clearCashRegisterForm,

    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
    openViewModal,
    closeViewModal,
    toggleCashRegisterStatus,
    setCashRegisterDropdown

} from "../../store/slices/cashRegister/cashRegisterSlice";

import useAlerts from "../useAlerts";
import useLoading from "../useLoading";
import useApiErrorHandler from "../useApiErrorHandler";

export const useCashRegister = () => {

    const dispatch: AppDispatch = useDispatch();

    const {
        list,
        dropdown,
        selected,
        form,
        loading,
        showCreateModal,
        showEditModal,
        showViewModal
    } = useSelector((state: RootState) => state.cashRegister);

    const { show: showAlert } = useAlerts();
    const { handle } = useApiErrorHandler();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();

    /**
     * 📋 Obtener todas las terminales POS
     */
    const fetchRegisters = async () => {
        try {

            showLoadingIndicator();
            dispatch(setLoading(true));

            const response = await CashRegisterService.getAll();

            if (response?.statusCode === 200) {
                dispatch(setCashRegisters(response.data));
            }

        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
            dispatch(setLoading(false));
        }
    };

    /**
 * 🔽 Obtener terminales POS para dropdown
 */
    const fetchDropdown = async () => {
        try {

            const response = await CashRegisterService.dropdown();

            if (response?.statusCode === 200) {
                dispatch(setCashRegisterDropdown(response.data));
            }

        } catch (error) {
            handle(error);
        }
    };

    /**
     * 🔎 Obtener terminal POS por ID
     */
    const fetchRegisterById = async (id: number) => {
        try {

            showLoadingIndicator();

            const response = await CashRegisterService.getById(id);

            if (response?.statusCode === 200) {

                dispatch(setSelectedCashRegister(response.data));
                dispatch(setCashRegisterForm(response.data));
            }

        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    /**
     * ➕ Crear terminal POS
     */
    const createRegister = async (data: any) => {
        try {

            showLoadingIndicator();

            const response = await CashRegisterService.create(data);

            if (response?.statusCode === 200 || response?.statusCode === 201) {

                showAlert(
                    response.type,
                    response.title,
                    response.message
                );

                dispatch(closeCreateModal());
                dispatch(clearCashRegisterForm());

                await fetchRegisters();
            }

        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    /**
     * ✏️ Actualizar terminal POS
     */
    const updateRegister = async (id: number, data: any) => {
        try {

            showLoadingIndicator();

            const response = await CashRegisterService.update(id, data);

            if (response?.statusCode === 200) {

                showAlert(
                    response.type,
                    response.title,
                    response.message
                );

                dispatch(closeEditModal());
                dispatch(clearCashRegisterForm());

                await fetchRegisters();
            }

        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    /**
     * 🚫 Desactivar terminal POS
     */
    const deactivateRegister = async (id: number) => {
        try {

            showLoadingIndicator();

            const response = await CashRegisterService.deactivate(id);

            if (response?.statusCode === 200) {

                dispatch(toggleCashRegisterStatus(id));

                showAlert(
                    response.type,
                    response.title,
                    response.message
                );
            }

        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    /**
     * 🎯 Seleccionar terminal
     */
    const selectRegister = (register: any) => {
        dispatch(setSelectedCashRegister(register));
    };

    /**
     * ✏️ Actualizar campo del formulario
     */
    const updateField = (field: string, value: any) => {

        dispatch(updateCashRegisterField({
            field,
            value
        }));
    };

    /**
     * 🧠 Establecer formulario
     */
    const setForm = (data: any) => {
        dispatch(setCashRegisterForm(data));
    };

    /**
     * 🧹 Limpiar formulario
     */
    const clearForm = () => {
        dispatch(clearCashRegisterForm());
    };

    /**
     * 📂 Modales
     */
    const openCreate = () => dispatch(openCreateModal());
    const closeCreate = () => dispatch(closeCreateModal());

    const openEdit = () => dispatch(openEditModal());
    const closeEdit = () => dispatch(closeEditModal());

    const openView = () => dispatch(openViewModal());
    const closeView = () => dispatch(closeViewModal());

    return {

        list,
        dropdown,
        selected,
        form,
        loading,

        showCreateModal,
        showEditModal,
        showViewModal,

        fetchRegisters,
        fetchDropdown,
        fetchRegisterById,

        createRegister,
        updateRegister,
        deactivateRegister,

        selectRegister,

        updateField,
        setForm,
        clearForm,

        openCreate,
        closeCreate,

        openEdit,
        closeEdit,

        openView,
        closeView
    };
};